import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderRadiusProps, SizeProps } from 'styled-system';
import { borderRadius, height, size, space, themeGet, width } from 'styled-system';

import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import ResponsiveImage from 'common/components/ResponsiveImage';
import { BodyBoldBB1, BodyRegularBR2 } from 'common/components/Typography';
import type { FillProps, SpaceProps } from 'common/theme/system-types';
import type { TextDecorationProps } from 'common/theme/system-utilities';
import { fill, textDecoration } from 'common/theme/system-utilities';
import type { PromoImgV2 } from 'common/types/NavigationItem';
import { trackPromoImageClick } from './tracking';

const ICON_SIZE = 20;
const PROMO_IMAGE_WIDTH = 284;

const PromotionImage = styled(ResponsiveImage)<SpaceProps>`
  ${height};
  ${space};
  ${width};
`;

const promotionImageStyles: Partial<ComponentProps<typeof PromotionImage>> = {
  width: '100%',
  height: 'auto',
  mb: 'sp_8',
};

const Container = styled.div<SpaceProps & TextDecorationProps & BorderRadiusProps>`
  ${borderRadius};
  ${space};
  ${textDecoration};

  &:hover {
    outline: 1px solid ${themeGet('colors.sys.neutral.border.hover')};
  }

  &:active {
    outline: 1px solid ${themeGet('colors.sys.neutral.border.inactive')};
  }

  &:hover div,
  &:hover svg {
    color: ${themeGet('colors.sys.neutral.text.hover')};
    fill: ${themeGet('colors.sys.neutral.text.hover')};
  }

  &:active div,
  &:active svg {
    color: ${themeGet('colors.sys.neutral.text.inactive')};
    fill: ${themeGet('colors.sys.neutral.text.inactive')};
  }
`;

const containerStyles: ComponentProps<typeof Container> = {
  as: 'a',
  borderRadius: 'medium',
  p: 'sp_8',
  textDecoration: 'none',
};

const ArrowIcon = styled(Icon)<FillProps & SizeProps>`
  ${fill};
  ${size};
`;

const arrowIconStyles: ComponentProps<typeof ArrowIcon> = {
  name: 'arrowRight',
  fill: 'sys.neutral.icon.default',
  size: ICON_SIZE,
};

type PromoImageProps = PromoImgV2 & PropsWithChildren;

const PromoImage = ({ ctaText, headlineText, ctaLink, children, ...imgData }: PromoImageProps) => (
  <Container
    {...(ctaLink?.fullUrl
      ? {
          href: ctaLink?.fullUrl,
          onClick: () => trackPromoImageClick(headlineText),
        }
      : {})}
    {...containerStyles}
  >
    <PromotionImage
      {...imgData}
      isLazy
      widths={PROMO_IMAGE_WIDTH}
      alt={headlineText}
      src={imgData.x1}
      {...promotionImageStyles}
    />
    <BodyBoldBB1 pb="sp_6">{headlineText}</BodyBoldBB1>
    {ctaText && (
      <Flex>
        <BodyRegularBR2>{ctaText}</BodyRegularBR2>
        <ArrowIcon {...arrowIconStyles} />
      </Flex>
    )}
  </Container>
);

export default PromoImage;
